import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type { Product, ProductQueryParams, CreateProductData, UpdateProductData } from '../types';

/**
 * Build query string from product query parameters.
 */
function buildQueryString(params?: ProductQueryParams): string {
  if (!params) return '';

  const searchParams = new URLSearchParams();
  if (params.barcode) searchParams.set('barcode', params.barcode);
  if (params.name) searchParams.set('name', params.name);
  if (params.category) searchParams.set('category', params.category);
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.offset) searchParams.set('offset', params.offset.toString());

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Fetch products with optional search/filter parameters.
 */
async function getProducts(params?: ProductQueryParams): Promise<Product[]> {
  const queryString = buildQueryString(params);
  return apiFetch<Product[]>(`/products${queryString}`);
}

/**
 * Fetch a single product by ID.
 */
async function getProduct(id: string): Promise<Product> {
  return apiFetch<Product>(`/products/${id}`);
}

/**
 * Fetch a product by barcode.
 * This endpoint includes Open Food Facts fallback on the server.
 */
async function getProductByBarcode(barcode: string): Promise<Product> {
  return apiFetch<Product>(`/products/barcode/${barcode}`);
}

/**
 * Create a new product.
 */
async function createProduct(data: CreateProductData): Promise<Product> {
  return apiFetch<Product>('/products', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing product.
 */
async function updateProduct(id: string, data: UpdateProductData): Promise<Product> {
  return apiFetch<Product>(`/products/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete a product.
 */
async function deleteProduct(id: string): Promise<void> {
  await apiFetch(`/products/${id}`, { method: 'DELETE' });
}

/**
 * Contribute a user translation for a product name.
 */
async function contributeTranslation(
  productId: string,
  language: string,
  name: string
): Promise<Product> {
  return apiFetch<Product>(`/products/${productId}/translations`, {
    method: 'POST',
    body: JSON.stringify({ language, name }),
  });
}

/**
 * Hook to fetch products with optional search/filter parameters.
 * Uses a 2 minute stale time for product catalog data.
 */
export function useProducts(params?: ProductQueryParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.products.list(params),
    queryFn: () => getProducts(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: keepPreviousData, // Keep showing old results while fetching new ones
    enabled: options?.enabled,
  });
}

/**
 * Hook to fetch a single product by ID.
 */
export function useProduct(id: string) {
  return useQuery({
    queryKey: queryKeys.products.detail(id),
    queryFn: () => getProduct(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a product by barcode.
 * Includes Open Food Facts fallback on the server side.
 * Use `enabled` option to control when the query runs (e.g., after barcode scan).
 */
export function useProductByBarcode(barcode: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.products.barcode(barcode),
    queryFn: () => getProductByBarcode(barcode),
    enabled: options?.enabled !== false && !!barcode,
    staleTime: 5 * 60 * 1000, // 5 minutes (product data from OFF is stable)
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to create a new product.
 * Invalidates the products list on success.
 */
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
    },
  });
}

/**
 * Hook to update a product.
 * Updates the detail cache and invalidates the list.
 * Note: Only works for products with source 'user' or 'manual'.
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductData }) => updateProduct(id, data),
    onSuccess: (updatedProduct, { id }) => {
      queryClient.setQueryData(queryKeys.products.detail(id), updatedProduct);
      queryClient.invalidateQueries({ queryKey: queryKeys.products.list() });
    },
  });
}

/**
 * Hook to delete a product.
 * Invalidates all product queries on success.
 * Note: Only works for products with source 'user' or 'manual'.
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
    },
  });
}

/**
 * Hook to contribute a user translation for a product name.
 * Works for any product including those from Open Food Facts.
 * Updates product cache on success.
 */
export function useContributeTranslation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      language,
      name,
    }: {
      productId: string;
      language: string;
      name: string;
    }) => contributeTranslation(productId, language, name),
    onSuccess: (updatedProduct) => {
      // Update the product in cache
      queryClient.setQueryData(queryKeys.products.detail(updatedProduct.id), updatedProduct);
      // Also update barcode query if it exists
      if (updatedProduct.barcode) {
        queryClient.setQueryData(
          queryKeys.products.barcode(updatedProduct.barcode),
          updatedProduct
        );
      }
      // Invalidate list queries to refresh search results
      queryClient.invalidateQueries({ queryKey: queryKeys.products.list() });
    },
  });
}
